import { get } from 'aws-amplify/api';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import type { UseQueryOptions } from 'react-query';
import { useQuery } from 'react-query';
import { type QueryKeys, queryKeys } from '@/constants/query-key';
import { handleMaintenance } from '@/modules/utils/sign-out-utils';
import * as paths from '@/routes/paths';
import { removeSettingState } from '@/state/callbackHelpers';

type StorybookApiMocks = Record<string, unknown>;

const getStorybookApiMocks = (): StorybookApiMocks | undefined =>
  Reflect.get(globalThis, '__STORYBOOK_API_MOCKS__') as StorybookApiMocks | undefined;

export const apiGet = async <T>(apiName: string, path: string, options?: object) => {
  const storybookApiMocks = getStorybookApiMocks();
  if (storybookApiMocks) {
    const key = `${apiName} ${path}`;
    if (key in storybookApiMocks) {
      const mocked = storybookApiMocks[key];
      if (mocked instanceof Error) {
        throw mocked;
      }

      return mocked as T;
    }

    if (path in storybookApiMocks) {
      const mocked = storybookApiMocks[path];
      if (mocked instanceof Error) {
        throw mocked;
      }

      return mocked as T;
    }
  }

  const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
  try {
    const { response } = get({
      apiName,
      path,
      options: {
        ...options,
        headers: {
          Authorization: authToken as string,
        },
      },
    });

    return await ((await response).body.json() as Promise<T>);
  } catch (error) {
    let statusCode = 0;

    if (error && typeof error === 'object' && 'response' in error) {
      const err = error as { response: { statusCode: number; body: string } };

      if (err.response?.statusCode) {
        statusCode = err.response.statusCode;
      }

      handleMaintenance(statusCode, err.response.body);
    }

    // // lambdaのタイムアウト、lambdaのメモリ超過
    if (statusCode === 502) {
      throw new Error('システムエラーが発生しました。時間を置いてリクエストしてください。');
    }

    if (statusCode === 403) {
      signOut()
        .then(() => {
          removeSettingState();
          window.location.replace(`/#${paths.login}`);
        })
        .catch(() => {});
    }

    throw new Error(error instanceof Error ? `${error.message} (${error?.name})` : '');
  }
};

export const useGenericQuery = <T>(
  key: QueryKeys,
  apiName: string,
  path: string,
  queryOptions: UseQueryOptions<T, Error> = {},
  options?: object,
) => {
  const res = useQuery<T, Error>({
    queryKey: [queryKeys[key], options],
    queryFn: async () => apiGet<T>(apiName, path, options),
    retry: false,
    refetchOnWindowFocus: false,
    cacheTime: 0,
    staleTime: 0,
    ...queryOptions,
  });

  return { ...res, isLoading: res.isLoading || res.isFetching };
};
